// src/components/album/GalleryEditorModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  UserCheck, 
  Package,
  Wrench,
  ShoppingBag,
  Building2,
  GraduationCap,
  Box,
  TrendingUp,
  Star,
  Settings,
  Grid,
  Eye,
  Heart,
  Upload,
  Plus,
  Trash2,
  Edit3,
  ShoppingCart,
  MessageSquare,
  ExternalLink,
  Lock,
  Users,
  Monitor,
  Tablet,
  Smartphone,
  Activity,
  Clock,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Tag,
  Globe,
  Mail,
  Image as ImageIcon,
  MessageCircle,
  Share2,
  Calendar,
  DollarSign,
  Palette,
  Layout,
  Layers
} from 'lucide-react';
import { TeamSettingsTab } from '../settings/shared/TeamSettings';

// Album categories definition
const ALBUM_CATEGORIES = {
  'photo-album': { label: 'Photo Album', icon: ImageIcon, color: '#3b82f6' },
  'teams': { label: 'Team Management', icon: Users, color: '#8b5cf6' },
  'menu': { label: 'Menu', icon: Tag, color: '#f59e0b' },
  'store': { label: 'Store', icon: ShoppingCart, color: '#10b981' },
  'listing': { label: 'Listings', icon: Grid, color: '#06b6d4' },
  'portfolio': { label: 'Portfolio', icon: Palette, color: '#ec4899' },
  'slides': { label: 'Slides', icon: Layout, color: '#6366f1' },
  'students': { label: 'Students', icon: Users, color: '#14b8a6' }
};

const ITEM_TYPES = [
  {
    id: 'member',
    label: 'Team Members',
    icon: Users,
    examples: 'Employees, Staff, Volunteers',
    defaultStatusTypes: ['wellbeing', 'attendance', 'performance']
  },
  {
    id: 'customer',
    label: 'Customers',
    icon: UserCheck,
    examples: 'Clients, Customers, Users',
    defaultStatusTypes: ['engagement', 'satisfaction', 'lifecycle']
  },
  {
    id: 'product',
    label: 'Products',
    icon: Package,
    examples: 'Inventory, Items, Goods',
    defaultStatusTypes: ['inventory', 'quality', 'lifecycle']
  },
  {
    id: 'asset',
    label: 'Assets',
    icon: Wrench,
    examples: 'Equipment, Tools, Resources',
    defaultStatusTypes: ['condition', 'availability', 'maintenance']
  },
  {
    id: 'service',
    label: 'Services',
    icon: ShoppingBag,
    examples: 'Offerings, Services, Programs',
    defaultStatusTypes: ['availability', 'demand', 'quality']
  },
  {
    id: 'organisation',
    label: 'Organisations',
    icon: Building2,
    examples: 'Partners, Vendors, Branches',
    defaultStatusTypes: ['partnership', 'performance', 'compliance']
  },
  {
    id: 'student',
    label: 'Students',
    icon: GraduationCap,
    examples: 'Learners, Pupils, Trainees',
    defaultStatusTypes: ['attendance', 'performance', 'wellbeing']
  },
  {
    id: 'project',
    label: 'Projects',
    icon: Box,
    examples: 'Initiatives, Tasks, Deliverables',
    defaultStatusTypes: ['progress', 'timeline', 'budget']
  }
];

const STATUS_TYPE_CONFIGS = {
  member: [
    {
      id: 'wellbeing',
      label: 'Wellbeing',
      icon: Heart,
      iconName: 'Heart',
      description: 'Track emotional and mental wellbeing',
      defaultOptions: [
        { id: 'engaged', label: 'Engaged & Happy', color: '#10b981', icon: 'Smile' },
        { id: 'neutral', label: 'Neutral', color: '#6b7280', icon: 'Meh' },
        { id: 'stressed', label: 'Stressed', color: '#f59e0b', icon: 'Frown' },
        { id: 'struggling', label: 'Struggling', color: '#ef4444', icon: 'AlertCircle' }
      ]
    },
    {
      id: 'attendance',
      label: 'Attendance',
      icon: CheckCircle,
      iconName: 'CheckCircle',
      description: 'Track presence and availability',
      defaultOptions: [
        { id: 'present', label: 'Present', color: '#10b981', icon: 'CheckCircle' },
        { id: 'absent', label: 'Absent', color: '#ef4444', icon: 'XCircle' },
        { id: 'late', label: 'Late', color: '#f59e0b', icon: 'Clock' },
        { id: 'remote', label: 'Remote', color: '#3b82f6', icon: 'Home' }
      ]
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track work performance and output',
      defaultOptions: [
        { id: 'exceeding', label: 'Exceeding', color: '#10b981', icon: 'TrendingUp' },
        { id: 'meeting', label: 'Meeting Expectations', color: '#3b82f6', icon: 'Check' },
        { id: 'developing', label: 'Developing', color: '#f59e0b', icon: 'Clock' },
        { id: 'needs-support', label: 'Needs Support', color: '#ef4444', icon: 'AlertCircle' }
      ]
    }
  ],
  customer: [
    {
      id: 'engagement',
      label: 'Engagement',
      icon: Activity,
      iconName: 'Activity',
      description: 'Track customer engagement level',
      defaultOptions: [
        { id: 'active', label: 'Active', color: '#10b981', icon: 'Zap' },
        { id: 'occasional', label: 'Occasional', color: '#3b82f6', icon: 'Activity' },
        { id: 'inactive', label: 'Inactive', color: '#6b7280', icon: 'Minus' },
        { id: 'churned', label: 'Churned', color: '#ef4444', icon: 'XCircle' }
      ]
    },
    {
      id: 'satisfaction',
      label: 'Satisfaction',
      icon: Star,
      iconName: 'Star',
      description: 'Track customer satisfaction',
      defaultOptions: [
        { id: 'delighted', label: 'Delighted', color: '#10b981', icon: 'Star' },
        { id: 'satisfied', label: 'Satisfied', color: '#3b82f6', icon: 'ThumbsUp' },
        { id: 'neutral', label: 'Neutral', color: '#6b7280', icon: 'Meh' },
        { id: 'dissatisfied', label: 'Dissatisfied', color: '#ef4444', icon: 'ThumbsDown' }
      ]
    },
    {
      id: 'lifecycle',
      label: 'Lifecycle Stage',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track customer journey stage',
      defaultOptions: [
        { id: 'prospect', label: 'Prospect', color: '#8b5cf6', icon: 'Eye' },
        { id: 'lead', label: 'Lead', color: '#3b82f6', icon: 'UserPlus' },
        { id: 'customer', label: 'Customer', color: '#10b981', icon: 'CheckCircle' },
        { id: 'advocate', label: 'Advocate', color: '#f59e0b', icon: 'Star' }
      ]
    }
  ],
  product: [
    {
      id: 'inventory',
      label: 'Inventory Status',
      icon: Package,
      iconName: 'Package',
      description: 'Track stock levels',
      defaultOptions: [
        { id: 'in-stock', label: 'In Stock', color: '#10b981', icon: 'CheckCircle' },
        { id: 'low-stock', label: 'Low Stock', color: '#f59e0b', icon: 'AlertTriangle' },
        { id: 'out-of-stock', label: 'Out of Stock', color: '#ef4444', icon: 'XCircle' },
        { id: 'discontinued', label: 'Discontinued', color: '#6b7280', icon: 'Ban' }
      ]
    },
    {
      id: 'quality',
      label: 'Quality',
      icon: Star,
      iconName: 'Star',
      description: 'Track product quality',
      defaultOptions: [
        { id: 'excellent', label: 'Excellent', color: '#10b981', icon: 'Star' },
        { id: 'good', label: 'Good', color: '#3b82f6', icon: 'Check' },
        { id: 'fair', label: 'Fair', color: '#f59e0b', icon: 'AlertCircle' },
        { id: 'poor', label: 'Poor', color: '#ef4444', icon: 'XCircle' }
      ]
    },
    {
      id: 'lifecycle',
      label: 'Product Lifecycle',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track product lifecycle stage',
      defaultOptions: [
        { id: 'new', label: 'New', color: '#8b5cf6', icon: 'Sparkles' },
        { id: 'active', label: 'Active', color: '#10b981', icon: 'CheckCircle' },
        { id: 'mature', label: 'Mature', color: '#3b82f6', icon: 'TrendingUp' },
        { id: 'phasing-out', label: 'Phasing Out', color: '#6b7280', icon: 'TrendingDown' }
      ]
    }
  ],
  asset: [
    {
      id: 'condition',
      label: 'Condition',
      icon: Wrench,
      iconName: 'Wrench',
      description: 'Track asset condition',
      defaultOptions: [
        { id: 'excellent', label: 'Excellent', color: '#10b981', icon: 'CheckCircle' },
        { id: 'good', label: 'Good', color: '#3b82f6', icon: 'Check' },
        { id: 'fair', label: 'Fair', color: '#f59e0b', icon: 'AlertTriangle' },
        { id: 'poor', label: 'Poor', color: '#ef4444', icon: 'XCircle' }
      ]
    },
    {
      id: 'availability',
      label: 'Availability',
      icon: CheckCircle,
      iconName: 'CheckCircle',
      description: 'Track asset availability',
      defaultOptions: [
        { id: 'available', label: 'Available', color: '#10b981', icon: 'CheckCircle' },
        { id: 'in-use', label: 'In Use', color: '#3b82f6', icon: 'Play' },
        { id: 'maintenance', label: 'Maintenance', color: '#f59e0b', icon: 'Wrench' },
        { id: 'retired', label: 'Retired', color: '#6b7280', icon: 'Archive' }
      ]
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: Wrench,
      iconName: 'Wrench',
      description: 'Track maintenance status',
      defaultOptions: [
        { id: 'up-to-date', label: 'Up to Date', color: '#10b981', icon: 'CheckCircle' },
        { id: 'due-soon', label: 'Due Soon', color: '#f59e0b', icon: 'Clock' },
        { id: 'overdue', label: 'Overdue', color: '#ef4444', icon: 'AlertCircle' },
        { id: 'in-progress', label: 'In Progress', color: '#3b82f6', icon: 'Wrench' }
      ]
    }
  ],
  service: [
    {
      id: 'availability',
      label: 'Availability',
      icon: CheckCircle,
      iconName: 'CheckCircle',
      description: 'Track service availability',
      defaultOptions: [
        { id: 'available', label: 'Available', color: '#10b981', icon: 'CheckCircle' },
        { id: 'limited', label: 'Limited', color: '#f59e0b', icon: 'Clock' },
        { id: 'unavailable', label: 'Unavailable', color: '#ef4444', icon: 'XCircle' },
        { id: 'seasonal', label: 'Seasonal', color: '#3b82f6', icon: 'Calendar' }
      ]
    },
    {
      id: 'demand',
      label: 'Demand',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track service demand',
      defaultOptions: [
        { id: 'high', label: 'High Demand', color: '#ef4444', icon: 'TrendingUp' },
        { id: 'moderate', label: 'Moderate', color: '#3b82f6', icon: 'Activity' },
        { id: 'low', label: 'Low', color: '#10b981', icon: 'TrendingDown' }
      ]
    },
    {
      id: 'quality',
      label: 'Service Quality',
      icon: Star,
      iconName: 'Star',
      description: 'Track service quality ratings',
      defaultOptions: [
        { id: 'excellent', label: 'Excellent', color: '#10b981', icon: 'Star' },
        { id: 'good', label: 'Good', color: '#3b82f6', icon: 'ThumbsUp' },
        { id: 'needs-improvement', label: 'Needs Improvement', color: '#f59e0b', icon: 'AlertCircle' }
      ]
    }
  ],
  organisation: [
    {
      id: 'partnership',
      label: 'Partnership Status',
      icon: Building2,
      iconName: 'Building2',
      description: 'Track partnership status',
      defaultOptions: [
        { id: 'active', label: 'Active Partner', color: '#10b981', icon: 'CheckCircle' },
        { id: 'prospect', label: 'Prospect', color: '#3b82f6', icon: 'Eye' },
        { id: 'inactive', label: 'Inactive', color: '#6b7280', icon: 'Pause' },
        { id: 'terminated', label: 'Terminated', color: '#ef4444', icon: 'XCircle' }
      ]
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track organizational performance',
      defaultOptions: [
        { id: 'exceeding', label: 'Exceeding', color: '#10b981', icon: 'TrendingUp' },
        { id: 'meeting', label: 'Meeting Expectations', color: '#3b82f6', icon: 'Check' },
        { id: 'below', label: 'Below Expectations', color: '#ef4444', icon: 'TrendingDown' }
      ]
    },
    {
      id: 'compliance',
      label: 'Compliance',
      icon: CheckCircle,
      iconName: 'CheckCircle',
      description: 'Track compliance status',
      defaultOptions: [
        { id: 'compliant', label: 'Compliant', color: '#10b981', icon: 'CheckCircle' },
        { id: 'pending', label: 'Pending Review', color: '#f59e0b', icon: 'Clock' },
        { id: 'non-compliant', label: 'Non-Compliant', color: '#ef4444', icon: 'AlertCircle' }
      ]
    }
  ],
  student: [
    {
      id: 'attendance',
      label: 'Attendance',
      icon: CheckCircle,
      iconName: 'CheckCircle',
      description: 'Track student attendance',
      defaultOptions: [
        { id: 'present', label: 'Present', color: '#10b981', icon: 'CheckCircle' },
        { id: 'absent', label: 'Absent', color: '#ef4444', icon: 'XCircle' },
        { id: 'late', label: 'Late', color: '#f59e0b', icon: 'Clock' },
        { id: 'excused', label: 'Excused', color: '#3b82f6', icon: 'FileText' }
      ]
    },
    {
      id: 'performance',
      label: 'Academic Performance',
      icon: TrendingUp,
      iconName: 'TrendingUp',
      description: 'Track academic progress',
      defaultOptions: [
        { id: 'excelling', label: 'Excelling', color: '#10b981', icon: 'Star' },
        { id: 'on-track', label: 'On Track', color: '#3b82f6', icon: 'Check' },
        { id: 'needs-support', label: 'Needs Support', color: '#f59e0b', icon: 'AlertCircle' },
        { id: 'at-risk', label: 'At Risk', color: '#ef4444', icon: 'AlertTriangle' }
      ]
    },
    {
      id: 'wellbeing',
      label: 'Wellbeing',
      icon: Heart,
      iconName: 'Heart',
      description: 'Track student wellbeing',
      defaultOptions: [
        { id: 'positive', label: 'Positive', color: '#10b981', icon: 'Smile' },
        { id: 'neutral', label: 'Neutral', color: '#6b7280', icon: 'Meh' },
        { id: 'concerned', label: 'Concerned', color: '#f59e0b', icon: 'Frown' },
        { id: 'requires-attention', label: 'Requires Attention', color: '#ef4444', icon: 'AlertCircle' }
      ]
    }
  ],
  project: [
    {
      id: 'progress',
      label: 'Progress',
      icon: Activity,
      iconName: 'Activity',
      description: 'Track project progress',
      defaultOptions: [
        { id: 'on-track', label: 'On Track', color: '#10b981', icon: 'CheckCircle' },
        { id: 'at-risk', label: 'At Risk', color: '#f59e0b', icon: 'AlertTriangle' },
        { id: 'delayed', label: 'Delayed', color: '#ef4444', icon: 'Clock' },
        { id: 'blocked', label: 'Blocked', color: '#6b7280', icon: 'Ban' }
      ]
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: Clock,
      iconName: 'Clock',
      description: 'Track timeline status',
      defaultOptions: [
        { id: 'ahead', label: 'Ahead of Schedule', color: '#10b981', icon: 'TrendingUp' },
        { id: 'on-schedule', label: 'On Schedule', color: '#3b82f6', icon: 'Check' },
        { id: 'behind', label: 'Behind Schedule', color: '#ef4444', icon: 'TrendingDown' }
      ]
    },
    {
      id: 'budget',
      label: 'Budget',
      icon: DollarSign,
      iconName: 'DollarSign',
      description: 'Track budget status',
      defaultOptions: [
        { id: 'under-budget', label: 'Under Budget', color: '#10b981', icon: 'TrendingDown' },
        { id: 'on-budget', label: 'On Budget', color: '#3b82f6', icon: 'DollarSign' },
        { id: 'over-budget', label: 'Over Budget', color: '#ef4444', icon: 'TrendingUp' }
      ]
    }
  ]
};


export const GalleryEditorModal = ({ 
  user,
  gallery, 
  albumSettings = {}, 
  onSave, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('category');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [newUploadEmail, setNewUploadEmail] = useState('');
  const [formData, setFormData] = useState({
    // Category configuration
    category: gallery?.categoryConfig?.category || albumSettings.albumCategory || 'photo-album',
    subcategory: gallery?.categoryConfig?.subcategory || albumSettings.albumSubcategory || null,
    
    // Display settings
    columnsBreakpoints: gallery?.categoryConfig?.columnsBreakpoints || {
      mobile: 1,
      tablet: 2,
      desktop: 3,
      wide: 4
    },
    cardStyle: gallery?.categoryConfig?.cardStyle || 'default',
    showTitles: gallery?.categoryConfig?.showTitles !== false,
    showDescriptions: gallery?.categoryConfig?.showDescriptions !== false,
    showContentArea: gallery?.categoryConfig?.showContentArea !== false,
    toggleContentArea: gallery?.categoryConfig?.toggleContentArea !== false,
    contentAreaPosition: gallery?.categoryConfig?.contentAreaPosition || 'bottom', // NEW
    imageAspectRatio: gallery?.categoryConfig?.imageAspectRatio || 'auto',
    
    // Interactions
    enableLikes: gallery?.categoryConfig?.enableLikes !== false,
    enableComments: gallery?.categoryConfig?.enableComments !== false,
    enableSharing: gallery?.categoryConfig?.enableSharing !== false,
    enableReactions: gallery?.categoryConfig?.enableReactions || false,
    enableVoiceNotes: gallery?.categoryConfig?.enableVoiceNotes || false,
    
    // Transactions
    enableBooking: gallery?.categoryConfig?.enableBooking || false,
    bookingMethods: gallery?.categoryConfig?.bookingMethods || {
      online: false,
      openchat: false,
      whatsapp: false
    },
    bookingUrl: gallery?.categoryConfig?.bookingUrl || '',
    bookingWhatsApp: gallery?.categoryConfig?.bookingWhatsApp || '',
    requireDateRange: gallery?.categoryConfig?.requireDateRange || false,
    enableShopping: gallery?.categoryConfig?.enableShopping || false,
    shoppingMethods: gallery?.categoryConfig?.shoppingMethods || {
      link: false,
      whatsapp: false,
      email: false
    },
    shoppingUrl: gallery?.categoryConfig?.shoppingUrl || '',
    shoppingWhatsApp: gallery?.categoryConfig?.shoppingWhatsApp || '',
    shoppingEmail: gallery?.categoryConfig?.shoppingEmail || '',

    // Access permissions
    galleryVisibility: gallery?.categoryConfig?.galleryVisibility || 'public',
    allowedRoles: gallery?.categoryConfig?.allowedRoles || [],
    requireAuth: gallery?.categoryConfig?.requireAuth || false,
    allowContributions: gallery?.categoryConfig?.allowContributions || false,
    contributorApproval: gallery?.categoryConfig?.contributorApproval || 'auto',

    galleryUploadPermissions: gallery?.categoryConfig?.galleryUploadPermissions || {
      enabled: false,
      mode: 'public',
      authorizedGalleryUsers: [],
      allowEditOwn: true,
      requireModeration: false
    },
    // Team-specific settings (if teams category)
    quickStatusOptions: gallery?.categoryConfig?.quickStatusOptions || {},
    teamRecordCategories: gallery?.categoryConfig?.teamRecordCategories || [],
    wellbeingAlerts: gallery?.categoryConfig?.wellbeingAlerts || {},
    itemType: gallery?.categoryConfig?.itemType || 'member',
    enabledStatusTypes: gallery?.categoryConfig?.enabledStatusTypes || [],
    statusTypeConfigs: gallery?.categoryConfig?.statusTypeConfigs || {},

    itemType: gallery?.categoryConfig?.itemType || 'member',
    enabledStatusTypes: gallery?.categoryConfig?.enabledStatusTypes || [],
    statusTypeConfigs: gallery?.categoryConfig?.statusTypeConfigs || {}
  });

  useEffect(() => {
    if (gallery) {
      setFormData({
        category: gallery?.categoryConfig?.category || albumSettings.albumCategory || 'photo-album',
        subcategory: gallery?.categoryConfig?.subcategory || albumSettings.albumSubcategory || null,
        columnsBreakpoints: gallery?.categoryConfig?.columnsBreakpoints || {
          mobile: 1,
          tablet: 2,
          desktop: 3,
          wide: 4
        },
        cardStyle: gallery?.categoryConfig?.cardStyle || 'default',
        showTitles: gallery?.categoryConfig?.showTitles !== false,
        showDescriptions: gallery?.categoryConfig?.showDescriptions !== false,
        showContentArea: gallery?.categoryConfig?.showContentArea !== false,
        toggleContentArea: gallery?.categoryConfig?.toggleContentArea !== false,
        contentAreaPosition: gallery?.categoryConfig?.contentAreaPosition || 'bottom',
        imageAspectRatio: gallery?.categoryConfig?.imageAspectRatio || 'auto',
        enableLikes: gallery?.categoryConfig?.enableLikes !== false,
        enableComments: gallery?.categoryConfig?.enableComments !== false,
        enableSharing: gallery?.categoryConfig?.enableSharing !== false,
        enableReactions: gallery?.categoryConfig?.enableReactions || false,
        enableVoiceNotes: gallery?.categoryConfig?.enableVoiceNotes || false,
        enableBooking: gallery?.categoryConfig?.enableBooking || false,
        bookingMethods: gallery?.categoryConfig?.bookingMethods || {
          online: false,
          openchat: false,
          whatsapp: false
        },
        bookingUrl: gallery?.categoryConfig?.bookingUrl || '',
        bookingWhatsApp: gallery?.categoryConfig?.bookingWhatsApp || '',
        requireDateRange: gallery?.categoryConfig?.requireDateRange || false,
        enableShopping: gallery?.categoryConfig?.enableShopping || false,
        shoppingMethods: gallery?.categoryConfig?.shoppingMethods || {
          link: false,
          whatsapp: false,
          email: false
        },
        shoppingUrl: gallery?.categoryConfig?.shoppingUrl || '',
        shoppingWhatsApp: gallery?.categoryConfig?.shoppingWhatsApp || '',
        shoppingEmail: gallery?.categoryConfig?.shoppingEmail || '',
        galleryVisibility: gallery?.categoryConfig?.galleryVisibility || 'public',
        allowedRoles: gallery?.categoryConfig?.allowedRoles || [],
        requireAuth: gallery?.categoryConfig?.requireAuth || false,
        allowContributions: gallery?.categoryConfig?.allowContributions || false,
        contributorApproval: gallery?.categoryConfig?.contributorApproval || 'auto',
        galleryUploadPermissions: gallery?.categoryConfig?.galleryUploadPermissions || {
          enabled: false,
          mode: 'public',
          authorizedGalleryUsers: [],
          allowEditOwn: true,
          requireModeration: false
        },
        quickStatusOptions: gallery?.categoryConfig?.quickStatusOptions || {},
        teamRecordCategories: gallery?.categoryConfig?.teamRecordCategories || [],
        wellbeingAlerts: gallery?.categoryConfig?.wellbeingAlerts || {},
        itemType: gallery?.categoryConfig?.itemType || 'member',
        enabledStatusTypes: gallery?.categoryConfig?.enabledStatusTypes || [],
        statusTypeConfigs: gallery?.categoryConfig?.statusTypeConfigs || {}
      });
    }
  }, [gallery, albumSettings]);

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
  };

  const addGalleryAuthorization = () => {
    if (!newUploadEmail.trim()) return;
    
    const email = newUploadEmail.trim().toLowerCase();
    if (formData.galleryUploadPermissions.authorizedGalleryUsers.some(u => u.email === email)) {
      alert('User already authorized');
      return;
    }

    handleInputChange('galleryUploadPermissions.authorizedGalleryUsers', [
      ...formData.galleryUploadPermissions.authorizedGalleryUsers,
      { email, displayName: user.displayName || email.split('@')[0] }
    ]);
    setNewUploadEmail('');
  };

  const removeGalleryAuthorization = (email) => {
    handleInputChange(
      'galleryUploadPermissions.authorizedGalleryUsers',
      formData.galleryUploadPermissions.authorizedGalleryUsers.filter(u => u.email !== email)
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const categoryConfig = {
        category: formData.category,
        subcategory: formData.subcategory,
        columnsBreakpoints: formData.columnsBreakpoints,
        cardStyle: formData.cardStyle,
        showTitles: formData.showTitles,
        showDescriptions: formData.showDescriptions,
        showContentArea: formData.showContentArea,
        toggleContentArea: formData.toggleContentArea,
        contentAreaPosition: formData.contentAreaPosition,
        imageAspectRatio: formData.imageAspectRatio,
        enableLikes: formData.enableLikes,
        enableComments: formData.enableComments,
        enableSharing: formData.enableSharing,
        enableReactions: formData.enableReactions,
        enableVoiceNotes: formData.enableVoiceNotes,
        enableBooking: formData.enableBooking,
        bookingUrl: formData.bookingUrl,
        bookingMethods: formData.bookingMethods,
        bookingWhatsApp: formData.bookingWhatsApp,
        enableShopping: formData.enableShopping,
        shoppingMethods: formData.shoppingMethods,
        shoppingUrl: formData.shoppingUrl,
        shoppingWhatsApp: formData.shoppingWhatsApp,
        shoppingEmail: formData.shoppingEmail,
        requireDateRange: formData.requireDateRange,
        galleryVisibility: formData.galleryVisibility,
        allowedRoles: formData.allowedRoles,
        requireAuth: formData.requireAuth,
        allowContributions: formData.allowContributions,
        contributorApproval: formData.contributorApproval,
        galleryUploadPermissions: formData.galleryUploadPermissions,
        quickStatusOptions: formData.quickStatusOptions,
        teamRecordCategories: formData.teamRecordCategories,
        wellbeingAlerts: formData.wellbeingAlerts,
        itemType: formData.itemType,
        enabledStatusTypes: formData.enabledStatusTypes,
        statusTypeConfigs: formData.statusTypeConfigs,
        lastUpdated: new Date().toISOString()
      };

      console.log('Saving gallery settings:', categoryConfig);
      await onSave(categoryConfig);
      setSaveStatus('success');
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Error saving gallery settings:', error);
      console.error('Error details:', error.message, error.code);
      setSaveStatus('error');
      alert(`Save failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'category', label: 'Category', icon: Tag },
    { id: 'itemStatus', label: 'Item & Status', icon: Layers },
    { id: 'display', label: 'Display', icon: Monitor },
    { id: 'interactions', label: 'Interactions', icon: Heart },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
    { id: 'permissions', label: 'Permissions', icon: Lock }
  ];

  // Add team tab if teams category selected
  if (formData.category === 'teams') {
    tabs.splice(3, 0, { id: 'team', label: 'Team Config', icon: Users });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gallery Settings
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure {gallery.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSaving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-56 border-r bg-gray-50 p-4 overflow-y-auto">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Category Tab */}
            {activeTab === 'category' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Gallery Category</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Choose the type of content this gallery will display
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(ALBUM_CATEGORIES).map(([key, category]) => {
                    const Icon = category.icon;
                    const isSelected = formData.category === key;
                    
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          handleInputChange('category', key);
                          // Reset subcategory when changing category
                          if (key !== 'teams') {
                            handleInputChange('subcategory', null);
                          }
                        }}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-current shadow-md'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{
                          backgroundColor: isSelected ? `${category.color}10` : '#ffffff',
                          borderColor: isSelected ? category.color : undefined
                        }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: `${category.color}20` }}
                          >
                            <Icon className="h-5 w-5" style={{ color: category.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{category.label}</span>
                        </div>
                        {isSelected && (
                          <CheckCircle className="h-4 w-4 ml-auto" style={{ color: category.color }} />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Item & Status Tab */}
                {activeTab === 'itemStatus' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Item Type & Status Configuration</h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Define what type of items this gallery contains and what statuses can be tracked
                      </p>
                    </div>

                    {/* Item Type Selection */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-900 mb-3">
                        Item Type
                      </label>
                      <p className="text-xs text-gray-600 mb-4">
                        What type of items does this gallery contain?
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {ITEM_TYPES.map((itemType) => {
                          const Icon = itemType.icon;
                          const isSelected = formData.itemType === itemType.id;
                          
                          return (
                            <button
                              key={itemType.id}
                              onClick={() => {
                                handleInputChange('itemType', itemType.id);
                                // Auto-select default status types for this item type
                                handleInputChange('enabledStatusTypes', itemType.defaultStatusTypes || []);
                              }}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <Icon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                                <div>
                                  <div className="font-medium text-gray-900">{itemType.label}</div>
                                  <div className="text-xs text-gray-500">{itemType.examples}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Status Types Configuration */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-900 mb-3">
                        Enabled Status Types
                      </label>
                      <p className="text-xs text-gray-600 mb-4">
                        Select which status types you want to track for {ITEM_TYPES.find(t => t.id === formData.itemType)?.label || 'items'}
                      </p>
                      
                      <div className="space-y-3">
                        {STATUS_TYPE_CONFIGS[formData.itemType]?.map((statusType) => {
                          const Icon = statusType.icon;
                          const isEnabled = formData.enabledStatusTypes.includes(statusType.id);
                          
                          return (
                            <div key={statusType.id} className="border border-gray-200 rounded-lg p-3">
                              <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={(e) => {
                                    const newEnabled = e.target.checked
                                      ? [...formData.enabledStatusTypes, statusType.id]
                                      : formData.enabledStatusTypes.filter(id => id !== statusType.id);
                                    handleInputChange('enabledStatusTypes', newEnabled);
                                    
                                    // Initialize config for this status type if enabled
                                    if (e.target.checked) {
                                      handleInputChange(`statusTypeConfigs.${statusType.id}`, {
                                        label: statusType.label,
                                        icon: statusType.iconName,
                                        options: statusType.defaultOptions || []
                                      });
                                    }
                                  }}
                                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-gray-600" />
                                    <span className="font-medium text-gray-900">{statusType.label}</span>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">{statusType.description}</p>
                                  
                                  {/* Show default options */}
                                  {isEnabled && statusType.defaultOptions && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {statusType.defaultOptions.map(option => (
                                        <span
                                          key={option.id}
                                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full"
                                          style={{ 
                                            backgroundColor: `${option.color}20`,
                                            color: option.color 
                                          }}
                                        >
                                          {option.label}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Quick Actions Info */}
                    {formData.enabledStatusTypes.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex gap-3">
                          <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Status Tracking Enabled</p>
                            <p className="text-xs text-blue-700 mt-1">
                              {formData.enabledStatusTypes.length} status type(s) will be available for quick updates on each {ITEM_TYPES.find(t => t.id === formData.itemType)?.label.toLowerCase() || 'item'}.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Team Subcategory Selection */}
                {formData.category === 'teams' && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Team Type
                    </label>
                    <select
                      value={formData.subcategory || 'corporate'}
                      onChange={(e) => handleInputChange('subcategory', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="corporate">🏢 Corporate Team</option>
                      <option value="sports">🏆 Sports Team</option>
                      <option value="project">📁 Project Team</option>
                      <option value="volunteer">❤️ Volunteer Group</option>
                    </select>
                    <p className="text-xs text-blue-700 mt-2">
                      Configure team-specific settings in the Team Config tab
                    </p>
                  </div>
                )}

                {/* Info about inherited settings */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-600">
                      <p className="font-medium mb-1">Category Override</p>
                      <p>This gallery's category is <strong>{ALBUM_CATEGORIES[formData.category]?.label}</strong>. It will use category-specific features and fields, overriding album-level defaults.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Display Tab */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Display Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure how cards appear in this gallery
                  </p>
                </div>

                {/* Columns per Breakpoint */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Columns by Device Size</h4>
                  <div className="space-y-4">
                    {[
                      { key: 'mobile', label: 'Mobile', icon: Smartphone, max: 2 },
                      { key: 'tablet', label: 'Tablet', icon: Tablet, max: 4 },
                      { key: 'desktop', label: 'Desktop', icon: Monitor, max: 6 },
                      { key: 'wide', label: 'Wide Screen', icon: Monitor, max: 8 }
                    ].map(({ key, label, icon: Icon, max }) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-gray-600" />
                          <span className="font-medium text-gray-900">{label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="1"
                            max={max}
                            value={formData.columnsBreakpoints[key]}
                            onChange={(e) => handleInputChange(`columnsBreakpoints.${key}`, parseInt(e.target.value))}
                            className="w-32"
                          />
                          <span className="w-8 text-center font-medium text-gray-900">
                            {formData.columnsBreakpoints[key]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Style */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Card Style</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'default', label: 'Default', desc: 'Standard card' },
                      { id: 'minimal', label: 'Minimal', desc: 'Clean & simple' },
                      { id: 'detailed', label: 'Detailed', desc: 'More info' }
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => handleInputChange('cardStyle', style.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.cardStyle === style.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-gray-900">{style.label}</div>
                        <div className="text-xs text-gray-600 mt-1">{style.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image Aspect Ratio */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Image Aspect Ratio</h4>
                  <select
                    value={formData.imageAspectRatio}
                    onChange={(e) => handleInputChange('imageAspectRatio', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="auto">Auto (Original)</option>
                    <option value="square">Square (1:1)</option>
                    <option value="landscape">Landscape (16:9)</option>
                    <option value="portrait">Portrait (3:4)</option>
                  </select>
                </div>

                {/* Display Options */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Display Options</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.showTitles}
                        onChange={(e) => handleInputChange('showTitles', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-900">Show Titles</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.showDescriptions}
                        onChange={(e) => handleInputChange('showDescriptions', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-900">Show Descriptions</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.showContentArea}
                        onChange={(e) => handleInputChange('showContentArea', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">Show Content Area</span>
                        <p className="text-xs text-gray-600 mt-0.5">Display expandable details section on cards</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.toggleContentArea}
                        onChange={(e) => handleInputChange('toggleContentArea', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">Allow Content Toggle</span>
                        <p className="text-xs text-gray-600 mt-0.5">When enabled, users can show/hide card details. When disabled, details are always visible.</p>
                      </div>
                    </label>

                    {/* NEW: Content Area Position */}
                    {formData.showContentArea && (
                      <div className="ml-6 p-3 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-2">Content Area Position</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleInputChange('contentAreaPosition', 'top')}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              formData.contentAreaPosition === 'top'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            Top
                          </button>
                          <button
                            onClick={() => handleInputChange('contentAreaPosition', 'bottom')}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              formData.contentAreaPosition === 'bottom'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            Bottom
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">Position content details above or below the media</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Team Config Tab */}
            {activeTab === 'team' && formData.category === 'teams' && (
              <TeamSettingsTab
                formData={formData}
                handleInputChange={handleInputChange}
              />
            )}

            {/* Interactions Tab */}
            {activeTab === 'interactions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Interaction Features</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable features for user engagement
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { key: 'enableLikes', label: 'Enable Likes', icon: Heart, desc: 'Allow users to like content' },
                    { key: 'enableComments', label: 'Enable Comments', icon: MessageCircle, desc: 'Allow users to comment' },
                    { key: 'enableSharing', label: 'Enable Sharing', icon: Share2, desc: 'Add share buttons' },
                    { key: 'enableReactions', label: 'Enable Reactions', icon: Heart, desc: 'Extended emoji reactions' },
                    { key: 'enableVoiceNotes', label: 'Enable Voice Notes', icon: Layers, desc: 'Allow voice comments' }
                  ].map((interaction) => {
                    const Icon = interaction.icon;
                    return (
                      <label
                        key={interaction.key}
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData[interaction.key]}
                          onChange={(e) => handleInputChange(interaction.key, e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-gray-600" />
                            <span className="font-medium text-gray-900">{interaction.label}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{interaction.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Features</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable booking and shopping functionality with multiple methods
                  </p>
                </div>

                {/* Booking - Multiple Selection */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enableBooking}
                      onChange={(e) => handleInputChange('enableBooking', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Enable Booking</span>
                    </div>
                  </label>
                  
                  {formData.enableBooking && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Booking Methods (Select Multiple)
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.bookingMethods?.online || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.bookingMethods, online: e.target.checked };
                                handleInputChange('bookingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <ExternalLink className="h-4 w-4 text-blue-600" />
                            <div>
                              <div className="font-medium text-gray-900">Online Link</div>
                              <div className="text-xs text-gray-500">Direct booking URL</div>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.bookingMethods?.openchat || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.bookingMethods, openchat: e.target.checked };
                                handleInputChange('bookingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <MessageSquare className="h-4 w-4 text-purple-600" />
                            <div>
                              <div className="font-medium text-gray-900">Openchat Widget</div>
                              <div className="text-xs text-gray-500">Chat-based booking</div>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.bookingMethods?.whatsapp || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.bookingMethods, whatsapp: e.target.checked };
                                handleInputChange('bookingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <MessageCircle className="h-4 w-4 text-green-600" />
                            <div>
                              <div className="font-medium text-gray-900">WhatsApp</div>
                              <div className="text-xs text-gray-500">Book via WhatsApp</div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {formData.bookingMethods?.online && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Booking URL
                          </label>
                          <input
                            type="url"
                            value={formData.bookingUrl}
                            onChange={(e) => handleInputChange('bookingUrl', e.target.value)}
                            placeholder="https://your-booking-link.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.bookingMethods?.whatsapp && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            WhatsApp Number
                          </label>
                          <input
                            type="tel"
                            value={formData.bookingWhatsApp}
                            onChange={(e) => handleInputChange('bookingWhatsApp', e.target.value)}
                            placeholder="+27123456789"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      <div className="border-t pt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.requireDateRange || false}
                            onChange={(e) => handleInputChange('requireDateRange', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <div className="font-medium text-gray-900">Require Date Range</div>
                            <div className="text-xs text-gray-500">Customer specifies start and end dates</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Shopping - Multiple Selection */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enableShopping}
                      onChange={(e) => handleInputChange('enableShopping', e.target.checked)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-gray-900">Enable Shopping</span>
                    </div>
                  </label>
                  
                  {formData.enableShopping && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Shopping Methods (Select Multiple)
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.shoppingMethods?.link || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.shoppingMethods, link: e.target.checked };
                                handleInputChange('shoppingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <ExternalLink className="h-4 w-4 text-green-600" />
                            <div>
                              <div className="font-medium text-gray-900">External Link</div>
                              <div className="text-xs text-gray-500">Direct to shop URL</div>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.shoppingMethods?.whatsapp || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.shoppingMethods, whatsapp: e.target.checked };
                                handleInputChange('shoppingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <MessageCircle className="h-4 w-4 text-green-600" />
                            <div>
                              <div className="font-medium text-gray-900">WhatsApp</div>
                              <div className="text-xs text-gray-500">Order via WhatsApp</div>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.shoppingMethods?.email || false}
                              onChange={(e) => {
                                const newMethods = { ...formData.shoppingMethods, email: e.target.checked };
                                handleInputChange('shoppingMethods', newMethods);
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <Mail className="h-4 w-4 text-blue-600" />
                            <div>
                              <div className="font-medium text-gray-900">Email</div>
                              <div className="text-xs text-gray-500">Order via email</div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {formData.shoppingMethods?.link && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Shopping URL
                          </label>
                          <input
                            type="url"
                            value={formData.shoppingUrl}
                            onChange={(e) => handleInputChange('shoppingUrl', e.target.value)}
                            placeholder="https://your-shop-link.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.shoppingMethods?.whatsapp && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            WhatsApp Number
                          </label>
                          <input
                            type="tel"
                            value={formData.shoppingWhatsApp}
                            onChange={(e) => handleInputChange('shoppingWhatsApp', e.target.value)}
                            placeholder="+27123456789"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.shoppingMethods?.email && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Shop Email
                          </label>
                          <input
                            type="email"
                            value={formData.shoppingEmail}
                            onChange={(e) => handleInputChange('shoppingEmail', e.target.value)}
                            placeholder="shop@yourbusiness.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Permissions Tab */}
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Permissions</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Control who can view and contribute to this gallery
                  </p>
                </div>

                {/* Gallery Visibility */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Gallery Visibility</h4>
                  <div className="space-y-2">
                    {[
                      { value: 'public', label: 'Public', desc: 'Anyone can view' },
                      { value: 'unlisted', label: 'Unlisted', desc: 'Only with link' },
                      { value: 'private', label: 'Private', desc: 'Only authorized users' }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.galleryVisibility === option.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="visibility"
                          checked={formData.galleryVisibility === option.value}
                          onChange={() => handleInputChange('galleryVisibility', option.value)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Upload Permissions */}
                <div className="border-t pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Upload className="h-5 w-5 text-purple-600" />
                    <h4 className="font-medium text-gray-900">Upload Permissions</h4>
                  </div>
                  
                  <label className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={formData.galleryUploadPermissions.enabled}
                      onChange={(e) => handleInputChange('galleryUploadPermissions.enabled', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Allow User Uploads</div>
                      <div className="text-sm text-gray-600">Let users contribute media to this gallery</div>
                    </div>
                  </label>

                  {formData.galleryUploadPermissions.enabled && (
                    <div className="space-y-4 ml-4 pl-4 border-l-2 border-purple-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Who can upload?</label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              checked={formData.galleryUploadPermissions.mode === 'public'}
                              onChange={() => handleInputChange('galleryUploadPermissions.mode', 'public')}
                              className="mt-1 text-purple-600 focus:ring-purple-500"
                            />
                            <div>
                              <div className="font-medium text-gray-900">All Authenticated Users</div>
                              <div className="text-sm text-gray-500">Any signed-in user can upload</div>
                            </div>
                          </label>

                          <label className="flex items-start gap-3 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              checked={formData.galleryUploadPermissions.mode === 'authorized'}
                              onChange={() => handleInputChange('galleryUploadPermissions.mode', 'authorized')}
                              className="mt-1 text-purple-600 focus:ring-purple-500"
                            />
                            <div>
                              <div className="font-medium text-gray-900">Authorized Users Only</div>
                              <div className="text-sm text-gray-500">Only specific users can upload</div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {formData.galleryUploadPermissions.mode === 'authorized' && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-purple-600" />
                            <h5 className="font-medium text-gray-900">Authorized Uploaders</h5>
                          </div>

                          <div className="flex gap-2 mb-3">
                            <input
                              type="email"
                              value={newUploadEmail}
                              onChange={(e) => setNewUploadEmail(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addGalleryAuthorization()}
                              placeholder="user@example.com"
                              className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                            />
                            <button
                              onClick={addGalleryAuthorization}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Add
                            </button>
                          </div>

                          {formData.galleryUploadPermissions.authorizedGalleryUsers.length > 0 ? (
                            <div className="space-y-2">
                              {formData.galleryUploadPermissions.authorizedGalleryUsers.map((user) => (
                                <div key={user.email} className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-200">
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-purple-600" />
                                    <span className="text-sm text-gray-900">{user.email}</span>
                                  </div>
                                  <button
                                    onClick={() => removeGalleryAuthorization(user.email)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-sm text-gray-500">No authorized uploaders yet</div>
                          )}
                        </div>
                      )}

                      <label className="flex items-center gap-3 p-3 bg-white border rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.galleryUploadPermissions.allowEditOwn}
                          onChange={(e) => handleInputChange('galleryUploadPermissions.allowEditOwn', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-2">
                          <Edit3 className="h-4 w-4 text-purple-600" />
                          <div>
                            <div className="font-medium text-gray-900">Allow Edit Own Uploads</div>
                            <div className="text-sm text-gray-500">Users can edit/delete their own uploads</div>
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-white border rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.galleryUploadPermissions.requireModeration}
                          onChange={(e) => handleInputChange('galleryUploadPermissions.requireModeration', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Require Moderation</div>
                          <div className="text-sm text-gray-500">Review uploads before they appear</div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* Contributor Settings (existing) */}
                <div className="border-t pt-6">
                  <h4 className="font-medium text-gray-900 mb-4">Contributor Settings</h4>
                  
                  <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={formData.allowContributions}
                      onChange={(e) => handleInputChange('allowContributions', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Allow Contributions</div>
                      <div className="text-sm text-gray-600">Let others upload to this gallery</div>
                    </div>
                  </label>

                  {formData.allowContributions && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Approval Process</label>
                      <select
                        value={formData.contributorApproval}
                        onChange={(e) => handleInputChange('contributorApproval', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="auto">Auto-approve contributions</option>
                        <option value="manual">Require manual approval</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            {saveStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Settings saved successfully!</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Failed to save settings</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};